
import PropTypes from "prop-types";
import { FaTimes } from "react-icons/fa";
import React, { useState } from "react";
import { toast } from "react-toastify";
import InputField from "../../SharedComponent/Fields/InputField";
import TextareaField from "../../SharedComponent/Fields/TextareaField";
import Button from "../../SharedComponent/Button/Button";
import { API_END_POINTS } from "../../network/apiEndPoint";
import { postProtected } from "../../network/ApiService";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import HeroSection from "../HeroSection/HeroSection";

const initialWarehouse = {
  name: "",
  manager: "",
  capacity: "",
  description: "",
  warehouse_city: "",
  warehouse_country: "",
  warehouse_street: "",
  warehouse_zip:  "" , 
};

const CreateWarehousePage = ({ show, onClose, onSubmit, fetchWarehouses }) => {
    const { t } = useTranslation();
    const [newWarehouse, setNewWarehouse] = useState({ ...initialWarehouse });
    const navigate = useNavigate();
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewWarehouse((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateWarehouse = async (e) => {
    e.preventDefault();
    try {
      const url = API_END_POINTS.warehouses;
      if (!url) {
        throw new Error('API endpoint not defined');
      }

      const warehouseData = {
        ...newWarehouse,
        capacity: parseInt(newWarehouse.capacity, 10),
        manager: parseInt(newWarehouse.manager, 10)
      };
      
      const response = await postProtected(url, warehouseData);
      
      if (response) {
        setNewWarehouse({ ...initialWarehouse });
        toast.success(t("warehouse.createSuccess"));
        navigate('/dashboard'); 
        
      }
    } catch (error) {
       if (error.response?.status === 401) {
              toast.error(t("dashboardToasters.sessionExpiration"));
              navigate("/login");
              return;
            }
        const nameError = error?.response?.data?.name?.[0];
        console.log("name error " , nameError) 

        if (nameError === "warehouse with this name already exists.") {
          toast.error(t("warehouse.table.duplicateNameError")); 
        } else {
          toast.error(nameError || t("warehouse.createError"));
        }
    }
  };
  







  



  return (
     <div className="flex justify-center items-center max-h-screen bg-gray-50 pt-20 ">
       <HeroSection t={t} showText = {false} />
     <div className=" w-full max-w-4xl bg-white rounded-lg shadow-md p-6 md:p-4">
  <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-gray-800">
    {t("warehouse.createTitle")}
  </h2>
  
  <div className="space-y-5 ">
    {/* Basic Information Section */}
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
        {t("warehouse.table.basicInformation")}
      </h3>
      
      {/* Name and Capacity Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {t("warehouse.nameLabel")} <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            name="name"
            value={newWarehouse.name}
            onChange={handleChange}
            required
            placeholder={t("warehouse.namePlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">
            {t("warehouse.capacityLabel")} <span className="text-red-500">*</span>
          </label>
          <input
            id="capacity"
            name="capacity"
            type="number"
            value={newWarehouse.capacity}
            onChange={handleChange}
            required
            placeholder={t("warehouse.capacityPlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>
      </div>

      {/* Description Field */}
      
    </div>

    {/* Address Information Section */}
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
        Address Information
      </h3>
      
      {/* All Address Fields in One Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Street Address Field */}
        <div>
          <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
            {t("warehouse.streetLabel")} <span className="text-red-500">*</span>
          </label>
          <input
            id="street"
            name="warehouse_street"
            maxLength={20}
            value={newWarehouse.warehouse_street}
            onChange={handleChange}
            required
            placeholder={t("warehouse.streetPlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>

        {/* City Field */}
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
            {t("warehouse.cityLabel")} <span className="text-red-500">*</span>
          </label>
          <input
            id="city"
            maxLength={20}
            name="warehouse_city"
            value={newWarehouse.warehouse_city}
            onChange={handleChange}
            required
            placeholder={t("warehouse.cityPlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>

        {/* ZIP Field */}
        <div>
          <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
            {t("warehouse.zipLabel")} <span className="text-red-500">*</span>
          </label>
          <input
            id="zip"
            min={0}
            max={999999}
            type="number"
            name="warehouse_zip"
            value={newWarehouse.warehouse_zip}
            onChange={handleChange}
            required
            placeholder={t("warehouse.zipPlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>

        {/* Country Field */}
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
            {t("warehouse.countryLabel")} <span className="text-red-500">*</span>
          </label>
          <input
            maxLength={20}
            id="country"
            name="warehouse_country"
            value={newWarehouse.warehouse_country}
            onChange={handleChange}
            required
            placeholder={t("warehouse.countryPlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>
      </div>
    </div>

    {/* Action Buttons */}
    <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-4 border-t">
      <button
        type="button"
        onClick={() => navigate("/dashboard")}
        className="w-full sm:w-auto px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        {t("warehouse.cancelButton")}
      </button>
      <button
        type="submit"
        onClick={handleCreateWarehouse}
        className="w-full sm:w-auto px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        {t("warehouse.createButton")}
      </button>
    </div>
  </div>
</div>
    </div>
  );
};



export default CreateWarehousePage;